import React, { Children, cloneElement, isValidElement } from "react";
import { BAY_TYPES } from "../../../utils/bayGeometry";

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, Number.isFinite(v) ? v : lo));
const OB = 18;
const normDeg = (d) => {
  const n = ((Number(d) + 180) % 360 + 360) % 360;
  return n - 180;
};

const isBayTrickleVentKey = (key) => {
  const k = String(key || "");
  return k.startsWith("vent-") || k.startsWith("vg-") || k.startsWith("vr-") || k.startsWith("vgh-");
};

const splitBayFlatContent = (content) => {
  const glass = [];
  const nonGlass = [];
  const vents = [];
  for (const el of content || []) {
    if (!el) continue;
    const k = el.key != null ? String(el.key) : "";
    if (k.startsWith("pg-")) glass.push(el);
    else if (isBayTrickleVentKey(k)) vents.push(el);
    else nonGlass.push(el);
  }
  return { glass, nonGlass, vents };
};

const bayHeadSourcePadY = (extTopPx) => Math.max(OB + 4, Math.max(0, Number(extTopPx) || 0) + 8);

const isBayCurvedBaseExcludedKey = (key) => {
  const k = String(key || "");
  return k === "shd" || k === "bay-bg-shd" || k === "p-shd" || k === "asm-shd";
};

export function renderSharedBayPresentation({
  enabled = false,
  skipBayProjection = false,
  rootCell,
  presRoot,
  bayGeometry = null,
  mirror = false,
  ox,
  oy,
  ww,
  wh,
  idPrefix = "wg",
  frameColor = null,
  darken,
  lighten,
  mm2px,
  resolveBayPresentationDepthMult,
  bayPresentationDepthMult = null,
  bayPresentationStarkness = null,
  isCellTree,
  isRectUnionRoot,
  renderFlatWindow,
  includeAccessories = false,
  extTopPx = 0,
  extBottomPx = 0,
  extLeftPx = 0,
  extRightPx = 0,
  cillOn = false,
  cillPx = 0,
  cillStub = false,
  cillDepthMm = 0,
  view = "external",
}) {
  if (!enabled || skipBayProjection) return null;
  if (!bayGeometry || !Array.isArray(bayGeometry.facets) || bayGeometry.facets.length < 1) return null;

  const flat = renderFlatWindow({
    ww,
    wh,
    rootCell,
    ox,
    oy,
    idPrefix: `${idPrefix}-bay-flat`,
    bayGeometry,
  });
  const hasCurvedFace = !!(bayGeometry?.derived?.hasCurvedFace || bayGeometry?.hasCurvedFace);
  const bayRegionRoot = isRectUnionRoot(rootCell)
    ? rootCell?.regions?.[0]?.root
    : (isRectUnionRoot(presRoot) ? presRoot?.regions?.[0]?.root : presRoot);
  const facetRoot = bayRegionRoot || rootCell || presRoot;
  const facetNodes = (isCellTree(facetRoot) && facetRoot.split === "vertical" && Array.isArray(facetRoot.children))
    ? facetRoot.children
    : [];
  if (hasCurvedFace) {
    const curvedFaceNode = (isCellTree(facetNodes[0]) ? facetNodes[0] : null) || bayRegionRoot || rootCell;
    const curvedBaseRaw = renderFlatWindow({
      ww,
      wh,
      rootCell: curvedFaceNode,
      ox,
      oy,
      idPrefix: `${idPrefix}-curved`,
      bayGeometry: null,
    });
    const curvedBase = (Array.isArray(curvedBaseRaw) ? curvedBaseRaw : []).filter((el) => {
      const k = el && el.key != null ? String(el.key) : "";
      return !isBayCurvedBaseExcludedKey(k);
    });
    const frameFill = frameColor || "#ffffff";
    const accBehind = [];
    const accFront = [];
    if (includeAccessories) {
      if (extTopPx > 0) accBehind.push(<rect key="bay-curved-ext-top-raw" x={ox} y={oy - extTopPx} width={ww} height={extTopPx} fill={frameFill} />);
      if (extBottomPx > 0) accBehind.push(<rect key="bay-curved-ext-bot-raw" x={ox} y={oy + wh} width={ww} height={extBottomPx} fill={frameFill} />);
      if (extLeftPx > 0) accBehind.push(<rect key="bay-curved-ext-left-raw" x={ox - extLeftPx} y={oy} width={extLeftPx} height={wh} fill={frameFill} />);
      if (extRightPx > 0) accBehind.push(<rect key="bay-curved-ext-right-raw" x={ox + ww} y={oy} width={extRightPx} height={wh} fill={frameFill} />);
      if (cillOn && cillPx > 0) {
        const overhang = (view === "internal" || cillStub) ? 0 : Math.min(8, mm2px(cillDepthMm) * 0.04);
        accFront.push(
          <rect key="bay-curved-cill-raw" x={ox - overhang} y={oy + wh + (extBottomPx > 0 ? extBottomPx : 0)} width={ww + overhang * 2} height={cillPx} fill={frameFill} />
        );
      }
    }
    const bowAmp = clamp(Math.max(mm2px(Number(bayGeometry?.derived?.projection || 0)) * 0.22, ww * 0.06), 8, 34);
    const bowSign = view === "internal" ? -1 : 1;
    const cx = ox + ww / 2;
    const halfW = Math.max(1, ww / 2);
    const bowAtX = (x) => {
      const u = (Number(x) - cx) / halfW;
      const arch = Math.max(0, 1 - u * u);
      return bowSign * bowAmp * arch;
    };
    const warpPoint = (x, y) => ({ x: Number(x), y: Number(y) + bowAtX(x) });
    const parsePointList = (raw) => {
      const nums = String(raw || "").trim().split(/[\s,]+/).map((n) => Number(n)).filter((n) => Number.isFinite(n));
      const out = [];
      for (let i = 0; i + 1 < nums.length; i += 2) out.push({ x: nums[i], y: nums[i + 1] });
      return out;
    };
    const ptsToAttr = (pts) => pts.map((p) => `${p.x},${p.y}`).join(" ");
    const warpRectElement = (el) => {
      const p = el.props || {};
      const x = Number(p.x || 0);
      const y = Number(p.y || 0);
      const w = Math.max(0, Number(p.width || 0));
      const h = Math.max(0, Number(p.height || 0));
      if (w <= 0 || h <= 0) return null;
      const samples = Math.max(6, Math.min(20, Math.round(w / 28)));
      const topPts = [];
      const botPts = [];
      for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        const xx = x + w * t;
        topPts.push(warpPoint(xx, y));
        botPts.push(warpPoint(xx, y + h));
      }
      const poly = [...topPts, ...botPts.reverse()];
      const nextProps = { ...p };
      delete nextProps.x; delete nextProps.y; delete nextProps.width; delete nextProps.height;
      delete nextProps.rx; delete nextProps.ry;
      return <polygon key={el.key} {...nextProps} points={ptsToAttr(poly)} />;
    };
    const warpLineElement = (el) => {
      const p = el.props || {};
      const x1 = Number(p.x1 || 0), y1 = Number(p.y1 || 0);
      const x2 = Number(p.x2 || 0), y2 = Number(p.y2 || 0);
      const nextProps = { ...p };
      delete nextProps.x1; delete nextProps.y1; delete nextProps.x2; delete nextProps.y2;
      if (Math.abs(y2 - y1) <= 0.001 && Math.abs(x2 - x1) > 0.001) {
        const lo = Math.min(x1, x2), hi = Math.max(x1, x2);
        const samples = Math.max(8, Math.min(26, Math.round(Math.abs(hi - lo) / 20)));
        const pts = [];
        for (let i = 0; i <= samples; i++) {
          const t = i / samples;
          const xx = lo + (hi - lo) * t;
          pts.push(warpPoint(xx, y1));
        }
        return <polyline key={el.key} {...nextProps} fill="none" points={ptsToAttr(pts)} />;
      }
      const a = warpPoint(x1, y1);
      const b = warpPoint(x2, y2);
      return <line key={el.key} {...nextProps} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
    };
    const warpPointsElement = (el, closed = false) => {
      const p = el.props || {};
      const pts = parsePointList(p.points).map((pt) => warpPoint(pt.x, pt.y));
      const nextProps = { ...p, points: ptsToAttr(pts) };
      if (!closed && nextProps.fill == null) nextProps.fill = "none";
      return cloneElement(el, { ...nextProps });
    };
    const warpPathElement = (el) => {
      const p = el.props || {};
      const rawD = String(p.d || "");
      if (!rawD.trim()) return el;
      if (/[ACQSTacqst]/.test(rawD)) return el;
      const tokens = [];
      const re = /([MLHVZmlhvz])|([-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?)/g;
      let m;
      while ((m = re.exec(rawD)) !== null) tokens.push(m[1] || m[2]);
      if (!tokens.length) return el;
      let i = 0;
      let cmd = null;
      let cxRaw = 0;
      let cyRaw = 0;
      let sxRaw = 0;
      let syRaw = 0;
      const out = [];
      const pushWarpedSegment = (x0Raw, y0Raw, x1Raw, y1Raw, move = false) => {
        if (!Number.isFinite(x0Raw) || !Number.isFinite(y0Raw) || !Number.isFinite(x1Raw) || !Number.isFinite(y1Raw)) return;
        if (move) {
          const wp0 = warpPoint(x1Raw, y1Raw);
          out.push("M", `${wp0.x}`, `${wp0.y}`);
          return;
        }
        if (Math.abs(y1Raw - y0Raw) <= 0.001 && Math.abs(x1Raw - x0Raw) > 0.001) {
          const span = Math.abs(x1Raw - x0Raw);
          const samples = Math.max(8, Math.min(26, Math.round(span / 20)));
          for (let si = 1; si <= samples; si++) {
            const t = si / samples;
            const xx = x0Raw + (x1Raw - x0Raw) * t;
            const wp = warpPoint(xx, y0Raw);
            out.push("L", `${wp.x}`, `${wp.y}`);
          }
          return;
        }
        const wp1 = warpPoint(x1Raw, y1Raw);
        out.push("L", `${wp1.x}`, `${wp1.y}`);
      };
      const isCmd = (t) => typeof t === "string" && /^[MLHVZmlhvz]$/.test(t);
      const hasNum = () => i < tokens.length && !isCmd(tokens[i]);
      while (i < tokens.length) {
        if (isCmd(tokens[i])) cmd = tokens[i++];
        if (!cmd) break;
        const upper = cmd.toUpperCase();
        const rel = cmd !== upper;
        if (upper === "Z") {
          out.push("Z");
          cxRaw = sxRaw;
          cyRaw = syRaw;
          continue;
        }
        if (upper === "M" || upper === "L") {
          let first = upper === "M";
          while (i + 1 < tokens.length && hasNum()) {
            const xIn = Number(tokens[i++]);
            const yIn = Number(tokens[i++]);
            if (!Number.isFinite(xIn) || !Number.isFinite(yIn)) return el;
            const xRaw = rel ? (cxRaw + xIn) : xIn;
            const yRaw = rel ? (cyRaw + yIn) : yIn;
            if (first) {
              pushWarpedSegment(cxRaw, cyRaw, xRaw, yRaw, true);
            } else {
              pushWarpedSegment(cxRaw, cyRaw, xRaw, yRaw, false);
            }
            cxRaw = xRaw;
            cyRaw = yRaw;
            if (first) {
              sxRaw = xRaw;
              syRaw = yRaw;
              first = false;
            }
            if (upper === "L" && (i >= tokens.length || isCmd(tokens[i]))) break;
          }
          continue;
        }
        if (upper === "H") {
          while (hasNum()) {
            const xIn = Number(tokens[i++]);
            if (!Number.isFinite(xIn)) return el;
            const xRaw = rel ? (cxRaw + xIn) : xIn;
            const yRaw = cyRaw;
            pushWarpedSegment(cxRaw, cyRaw, xRaw, yRaw, false);
            cxRaw = xRaw;
          }
          continue;
        }
        if (upper === "V") {
          while (hasNum()) {
            const yIn = Number(tokens[i++]);
            if (!Number.isFinite(yIn)) return el;
            const xRaw = cxRaw;
            const yRaw = rel ? (cyRaw + yIn) : yIn;
            pushWarpedSegment(cxRaw, cyRaw, xRaw, yRaw, false);
            cyRaw = yRaw;
          }
          continue;
        }
        return el;
      }
      const dWarp = out.join(" ");
      if (!dWarp) return el;
      return cloneElement(el, { ...p, d: dWarp });
    };
    const warpElement = (el) => {
      if (!isValidElement(el)) return el;
      const t = el.type;
      if (t === "rect") return warpRectElement(el);
      if (t === "line") return warpLineElement(el);
      if (t === "polygon") return warpPointsElement(el, true);
      if (t === "polyline") return warpPointsElement(el, false);
      if (t === "path") return warpPathElement(el);
      if (el?.props && el.props.children != null) {
        const kids = Children.map(el.props.children, warpElement);
        return cloneElement(el, { ...el.props }, kids);
      }
      return el;
    };
    return [
      ...accBehind.map(warpElement),
      ...curvedBase.map(warpElement),
      ...accFront.map(warpElement),
    ].filter(Boolean);
  }

  const bayFaces = Array.isArray(bayGeometry.facets) ? bayGeometry.facets : [];
  const faceCount = bayFaces.length;
  if (faceCount < 2 || !Number.isFinite(faceCount)) return flat;

  const presentationFaceSourceIdx = mirror
    ? Array.from({ length: faceCount }, (_, i) => faceCount - 1 - i)
    : Array.from({ length: faceCount }, (_, i) => i);
  const widthsRaw = Array.from({ length: faceCount }, (_, i) => {
    const srcIdx = Number(presentationFaceSourceIdx[i]);
    return Math.max(1, Number(bayFaces[srcIdx]?.width || 0));
  });
  const sumW = widthsRaw.reduce((s, v) => s + v, 0) || faceCount;
  const widthsPx = widthsRaw.map((w) => (w / sumW) * ww);
  const cuts = [ox];
  for (let i = 0; i < widthsPx.length; i++) cuts.push(cuts[cuts.length - 1] + widthsPx[i]);
  cuts[cuts.length - 1] = ox + ww;
  const segments = [];
  for (let i = 0; i < faceCount; i++) {
    const x0 = cuts[i];
    const x1 = cuts[i + 1];
    if (!Number.isFinite(x0) || !Number.isFinite(x1) || x1 - x0 < 1) continue;
    segments.push({ i, x0, x1, w: x1 - x0 });
  }
  if (segments.length < 2) return flat;

  const builtFaces = [];
  for (const seg of segments) {
    const sourceIdx = Number(presentationFaceSourceIdx[seg.i]);
    const facetNode = isCellTree(facetNodes[sourceIdx]) ? facetNodes[sourceIdx] : null;
    if (!facetNode) return flat;
    const faceFlat = renderFlatWindow({
        ww: seg.w,
        wh,
        rootCell: facetNode,
        ox: seg.x0,
        oy,
        idPrefix: `${idPrefix}-face-${seg.i}`,
        bayGeometry: null,
    });
    const content = (Array.isArray(faceFlat) ? faceFlat : []).filter((el) => {
      const k = el && el.key != null ? String(el.key) : "";
      return k !== "shd" && k !== "bay-bg-shd";
    });
    const { glass, nonGlass, vents } = splitBayFlatContent(content);
    builtFaces.push({ seg, sourceIdx, glass, nonGlass, vents });
  }
  if (builtFaces.length < 2) return flat;

  const bayType = String(bayGeometry?.config?.bayType || "");
  const pfiRaw = Number(bayGeometry?.primaryFaceIndex);
  const primarySourceIdx = Number.isFinite(pfiRaw)
    ? clamp(Math.round(pfiRaw), 0, builtFaces.length - 1)
    : Math.floor((builtFaces.length - 1) / 2);
  const primaryIdx = builtFaces.findIndex((f) => Number(f?.sourceIdx) === primarySourceIdx);
  const resolvedPrimaryIdx = primaryIdx >= 0 ? primaryIdx : Math.floor((builtFaces.length - 1) / 2);
  const useSeamAnchor = bayType === BAY_TYPES.CUSTOM && (builtFaces.length % 2 === 0) && builtFaces.length >= 4;
  const anchorSeamIdx = useSeamAnchor ? Math.floor(builtFaces.length / 2) : null;
  const sourceIdxAt = (displayIdx) => Number(builtFaces[displayIdx]?.sourceIdx ?? displayIdx);
  const referenceRotation = useSeamAnchor
    ? ((Number(bayFaces[sourceIdxAt(anchorSeamIdx - 1)]?.rotation || 0) + Number(bayFaces[sourceIdxAt(anchorSeamIdx)]?.rotation || 0)) / 2)
    : Number(bayFaces[sourceIdxAt(resolvedPrimaryIdx)]?.rotation || 0);
  const boundaryX = Array.from({ length: builtFaces.length + 1 }, () => 0);
  const boundaryTopY = Array.from({ length: builtFaces.length + 1 }, () => oy);
  const relativeAbsRotation = (displayIdx) => {
    const sourceIdx = sourceIdxAt(displayIdx);
    const rot = Number(bayFaces[sourceIdx]?.rotation || 0) + (mirror ? 180 : 0);
    const ref = Number(referenceRotation || 0) + (mirror ? 180 : 0);
    return Math.abs(normDeg(rot - ref));
  };
  const faceScale = (segIdx) => {
    if (!useSeamAnchor && segIdx === resolvedPrimaryIdx) return 1;
    const rot = relativeAbsRotation(segIdx);
    return clamp(Math.cos((rot * Math.PI) / 180), 0.35, 0.75);
  };
  const depthMult = resolveBayPresentationDepthMult({ bayPresentationDepthMult, bayPresentationStarkness });
  const depthBase = clamp((8 + mm2px(Number(bayGeometry?.derived?.projection || 0)) * 0.3) * depthMult, 4, 72);
  const faceLift = (segIdx) => {
    if (!useSeamAnchor && segIdx === resolvedPrimaryIdx) return 0;
    const rot = relativeAbsRotation(segIdx);
    return depthBase * clamp(rot / 45, 0.45, 1.5);
  };
  const liftDir = mirror ? 1 : -1;
  if (useSeamAnchor) {
    boundaryX[anchorSeamIdx] = cuts[anchorSeamIdx];
    boundaryTopY[anchorSeamIdx] = oy;
    for (let i = anchorSeamIdx; i < builtFaces.length; i++) {
      const dx = builtFaces[i].seg.w * faceScale(i);
      const dy = faceLift(i);
      boundaryX[i + 1] = boundaryX[i] + dx;
      boundaryTopY[i + 1] = boundaryTopY[i] + (liftDir * dy);
    }
    for (let i = anchorSeamIdx - 1; i >= 0; i--) {
      const dx = builtFaces[i].seg.w * faceScale(i);
      const dy = faceLift(i);
      boundaryX[i] = boundaryX[i + 1] - dx;
      boundaryTopY[i] = boundaryTopY[i + 1] + (liftDir * dy);
    }
  } else {
    boundaryX[resolvedPrimaryIdx] = cuts[resolvedPrimaryIdx];
    boundaryTopY[resolvedPrimaryIdx] = oy;
    for (let i = resolvedPrimaryIdx; i < builtFaces.length; i++) {
      const dx = builtFaces[i].seg.w * faceScale(i);
      const dy = faceLift(i);
      boundaryX[i + 1] = boundaryX[i] + dx;
      boundaryTopY[i + 1] = boundaryTopY[i] + (liftDir * dy);
    }
    for (let i = resolvedPrimaryIdx - 1; i >= 0; i--) {
      const dx = builtFaces[i].seg.w * faceScale(i);
      const dy = faceLift(i);
      boundaryX[i] = boundaryX[i + 1] - dx;
      boundaryTopY[i] = boundaryTopY[i + 1] + (liftDir * dy);
    }
  }
  const origMid = useSeamAnchor
    ? cuts[anchorSeamIdx]
    : ((cuts[resolvedPrimaryIdx] + cuts[resolvedPrimaryIdx + 1]) / 2);
  const newMid = useSeamAnchor
    ? boundaryX[anchorSeamIdx]
    : ((boundaryX[resolvedPrimaryIdx] + boundaryX[resolvedPrimaryIdx + 1]) / 2);
  const shiftX = origMid - newMid;
  for (let i = 0; i < boundaryX.length; i++) boundaryX[i] += shiftX;
  const planes = builtFaces.map((face) => {
    const { seg } = face;
    const p0 = { x: boundaryX[seg.i], y: boundaryTopY[seg.i] };
    const p1 = { x: boundaryX[seg.i + 1], y: boundaryTopY[seg.i + 1] };
    const p2 = { x: boundaryX[seg.i + 1], y: boundaryTopY[seg.i + 1] + wh };
    const p3 = { x: boundaryX[seg.i], y: boundaryTopY[seg.i] + wh };
    const sw = Math.max(1, seg.w);
    const a = (p1.x - p0.x) / sw;
    const b = (p1.y - p0.y) / sw;
    const e = p0.x - a * seg.x0;
    const f = p0.y - b * seg.x0 - oy;
    return { seg, p0, p1, p2, p3, a, b, e, f, absRot: relativeAbsRotation(seg.i) };
  });
  planes.sort((l, r) => {
    if (!useSeamAnchor) {
      if (l.seg.i === resolvedPrimaryIdx && r.seg.i !== resolvedPrimaryIdx) return 1;
      if (r.seg.i === resolvedPrimaryIdx && l.seg.i !== resolvedPrimaryIdx) return -1;
    }
    return l.absRot - r.absRot;
  });
  const layout = {
    planes,
    boundaryX,
    boundaryTopY,
    primaryIdx: resolvedPrimaryIdx,
    useSeamAnchor,
    centerX: useSeamAnchor ? boundaryX[anchorSeamIdx] : ((boundaryX[resolvedPrimaryIdx] + boundaryX[resolvedPrimaryIdx + 1]) / 2),
  };

  const mkId = (tag, x0, x1) => `${idPrefix}-${tag}-${Math.round(x0)}-${Math.round(x1)}`;
  const out = [];
  if (includeAccessories) {
    const topPts = layout.boundaryX.map((x, i) => ({ x, y: layout.boundaryTopY[i] }));
    const botPts = layout.boundaryX.map((x, i) => ({ x, y: layout.boundaryTopY[i] + wh }));
    const accFill = frameColor || "#ffffff";
    const accEdge = darken(accFill, 18);
    if (extTopPx > 0) out.push(<polygon key="bay-ext-top" points={[...topPts.map((p) => ({ x: p.x, y: p.y - extTopPx })), ...topPts.slice().reverse()].map((p) => `${p.x},${p.y}`).join(" ")} fill={accFill} stroke={accEdge} strokeWidth="0.45" opacity="0.96" />);
    if (extBottomPx > 0) out.push(<polygon key="bay-ext-bot" points={[...botPts, ...botPts.map((p) => ({ x: p.x, y: p.y + extBottomPx })).slice().reverse()].map((p) => `${p.x},${p.y}`).join(" ")} fill={accFill} stroke={accEdge} strokeWidth="0.45" opacity="0.96" />);
    if (extLeftPx > 0) out.push(<polygon key="bay-ext-left" points={`${topPts[0].x - extLeftPx},${topPts[0].y} ${topPts[0].x},${topPts[0].y} ${botPts[0].x},${botPts[0].y} ${botPts[0].x - extLeftPx},${botPts[0].y}`} fill={accFill} stroke={accEdge} strokeWidth="0.45" opacity="0.96" />);
    if (extRightPx > 0) {
      const li = topPts.length - 1;
      out.push(<polygon key="bay-ext-right" points={`${topPts[li].x},${topPts[li].y} ${topPts[li].x + extRightPx},${topPts[li].y} ${botPts[li].x + extRightPx},${botPts[li].y} ${botPts[li].x},${botPts[li].y}`} fill={accFill} stroke={accEdge} strokeWidth="0.45" opacity="0.96" />);
    }
    if (cillOn && cillPx > 0) {
      const overhang = (view === "internal" || cillStub) ? 0 : Math.min(8, mm2px(cillDepthMm) * 0.04);
      const cillTop = botPts.map((p, i, arr) => (i === 0 ? { x: p.x - overhang, y: p.y } : (i === arr.length - 1 ? { x: p.x + overhang, y: p.y } : p)));
      const cillBot = cillTop.map((p) => ({ x: p.x, y: p.y + cillPx }));
      out.push(<polygon key="bay-cill" points={[...cillTop, ...cillBot.slice().reverse()].map((p) => `${p.x},${p.y}`).join(" ")} fill={accFill} stroke={accEdge} strokeWidth="0.5" opacity="0.98" />);
    }
  }

  const bayOuterPts = [...layout.boundaryX.map((x, i) => ({ x, y: layout.boundaryTopY[i] })), ...layout.boundaryX.map((x, i) => ({ x, y: layout.boundaryTopY[i] + wh })).reverse()];
  const bayShadowPts = bayOuterPts.map((p) => ({ x: p.x + 4, y: p.y + 4 }));
  const polyPath = (pts) => `M${pts.map((p) => `${p.x},${p.y}`).join(" L")} Z`;
  out.push(<path key="bay-assembly-shadow" d={`${polyPath(bayShadowPts)} ${polyPath(bayOuterPts)}`} fill="rgba(0,0,0,0.18)" fillRule="evenodd" />);

  const faceByIndex = new Map(builtFaces.map((f) => [f.seg.i, f]));
  for (const fp of layout.planes) {
    const face = faceByIndex.get(fp.seg.i);
    if (!face) continue;
    const clipId = mkId("bay-quad", fp.seg.x0, fp.seg.x1);
    const srcTightId = mkId("bay-src-tight", fp.seg.x0, fp.seg.x1);
    const srcLooseId = mkId("bay-src-loose", fp.seg.x0, fp.seg.x1);
    const isPrimaryFace = !layout.useSeamAnchor && fp.seg.i === layout.primaryIdx;
    const faceMidX = (fp.p0.x + fp.p1.x) / 2;
    const depthDir = faceMidX < layout.centerX ? -1 : 1;
    const depthDx = isPrimaryFace ? 0 : depthDir * 2.2;
    const depthDy = isPrimaryFace ? 0 : 2.1;
    const depthPts = `${fp.p0.x + depthDx},${fp.p0.y + depthDy} ${fp.p1.x + depthDx},${fp.p1.y + depthDy} ${fp.p2.x + depthDx},${fp.p2.y + depthDy} ${fp.p3.x + depthDx},${fp.p3.y + depthDy}`;
    const loosePadX = Math.max(24, Math.min(32, 24 + (isPrimaryFace ? 2 : 5) + fp.absRot * 0.06));
    const loosePadY = bayHeadSourcePadY(extTopPx);
    if (!isPrimaryFace) {
      out.push(<polygon key={`${clipId}-depth`} points={depthPts} fill="rgba(0,0,0,0.11)" opacity={clamp(fp.absRot / 92, 0.08, 0.2)} />);
    }
    out.push(
      <defs key={`${clipId}-def`}>
        <clipPath id={clipId}>
          <polygon points={`${fp.p0.x},${fp.p0.y} ${fp.p1.x},${fp.p1.y} ${fp.p2.x},${fp.p2.y} ${fp.p3.x},${fp.p3.y}`} />
        </clipPath>
        <clipPath id={srcTightId}>
          <rect x={fp.seg.x0} y={oy} width={Math.max(1, fp.seg.w)} height={wh} />
        </clipPath>
        <clipPath id={srcLooseId}>
          <rect x={fp.seg.x0 - loosePadX} y={oy - loosePadY} width={Math.max(1, fp.seg.w + loosePadX * 2)} height={Math.max(1, wh + loosePadY * 2)} />
        </clipPath>
      </defs>
    );
    out.push(
      <g key={`${clipId}-g`} clipPath={`url(#${clipId})`}>
        <g transform={`matrix(${fp.a} ${fp.b} 0 1 ${fp.e} ${fp.f})`}>
          <g clipPath={`url(#${srcLooseId})`}>{face.nonGlass}</g>
          <g clipPath={`url(#${srcTightId})`}>{face.glass}</g>
        </g>
      </g>
    );
    if (face.vents && face.vents.length) {
      out.push(
        <g key={`${clipId}-vents`} transform={`matrix(${fp.a} ${fp.b} 0 1 ${fp.e} ${fp.f})`}>
          <g clipPath={`url(#${srcLooseId})`}>{face.vents}</g>
        </g>
      );
    }
    if (!isPrimaryFace) {
      const leftEdgeMidX = (fp.p0.x + fp.p3.x) / 2;
      const rightEdgeMidX = (fp.p1.x + fp.p2.x) / 2;
      const useLeftAsOuter = Math.abs(leftEdgeMidX - layout.centerX) >= Math.abs(rightEdgeMidX - layout.centerX);
      const outerTop = useLeftAsOuter ? fp.p0 : fp.p1;
      const outerBot = useLeftAsOuter ? fp.p3 : fp.p2;
      const edgeHiDx = useLeftAsOuter ? 1 : -1;
      out.push(
        <g key={`${clipId}-edge-depth`} clipPath={`url(#${clipId})`}>
          <line x1={outerTop.x} y1={outerTop.y} x2={outerBot.x} y2={outerBot.y} stroke="rgba(0,0,0,0.16)" strokeWidth="0.9" opacity="0.8" />
          <line x1={outerTop.x + edgeHiDx} y1={outerTop.y} x2={outerBot.x + edgeHiDx} y2={outerBot.y} stroke="rgba(255,255,255,0.16)" strokeWidth="0.45" opacity="0.55" />
          <line x1={fp.p3.x} y1={fp.p3.y} x2={fp.p2.x} y2={fp.p2.y} stroke="rgba(0,0,0,0.12)" strokeWidth="0.55" opacity="0.7" />
        </g>
      );
    }
  }
  for (let i = 1; i < builtFaces.length; i++) {
    const top = { x: layout.boundaryX[i], y: layout.boundaryTopY[i] };
    const bot = { x: layout.boundaryX[i], y: layout.boundaryTopY[i] + wh };
    out.push(
      <g key={`bay-seam-${i}`}>
        <line x1={top.x - 1} y1={top.y} x2={bot.x - 1} y2={bot.y} stroke="rgba(0,0,0,0.12)" strokeWidth="2.1" opacity="0.72" />
        <line x1={top.x} y1={top.y} x2={bot.x} y2={bot.y} stroke={darken(frameColor || "#ffffff", 35)} strokeWidth="1.1" opacity="0.94" />
        <line x1={top.x + 1} y1={top.y} x2={bot.x + 1} y2={bot.y} stroke={lighten(frameColor || "#ffffff", 15)} strokeWidth="0.5" opacity="0.36" />
      </g>
    );
  }
  return out;
}

